import { BaseObserver, LogLevels } from '@powersync/common';
import { resolveSyncOptions } from './sync/options.js';
/**
 * @internal
 */
export class ConnectionManager extends BaseObserver {
    options;
    /**
     * Tracks active connection attempts
     */
    connectingPromise;
    /**
     * Tracks actively instantiating a streaming sync implementation.
     */
    syncStreamInitPromise;
    /**
     * Active disconnect operation. Calling disconnect multiple times
     * will resolve to the same operation.
     */
    disconnectingPromise;
    /**
     * Tracks the last parameters supplied to `connect` calls.
     * Calling `connect` multiple times in succession will result in:
     * - 1 pending connection operation which will be aborted.
     * - updating the last set of parameters while waiting for the pending
     *   attempt to be aborted
     * - internally connecting with the last set of parameters
     */
    pendingConnectionOptions;
    currentOptions;
    // The connector of the active connection. Retained alongside currentOptions so `connector` reflects
    // the live connection, not just the brief pending-options window before connecting starts.
    currentConnector;
    syncStreamImplementation;
    /**
     * Additional cleanup function which is called after the sync stream implementation
     * is disposed.
     */
    syncDisposer;
    /**
     * Subscriptions managed in this connection manager.
     *
     * On the web, these local subscriptions are merged across tabs by a shared worker.
     */
    locallyActiveSubscriptions = new Map();
    constructor(options) {
        super();
        this.options = options;
        this.connectingPromise = null;
        this.syncStreamInitPromise = null;
        this.disconnectingPromise = null;
        this.pendingConnectionOptions = null;
        this.syncStreamImplementation = null;
        this.currentOptions = null;
        this.currentConnector = null;
        this.syncDisposer = null;
    }
    get connector() {
        return this.currentConnector ?? this.pendingConnectionOptions?.connector ?? null;
    }
    get connectionOptions() {
        return this.currentOptions ?? this.pendingConnectionOptions?.options ?? null;
    }
    get logger() {
        return this.options.logger;
    }
    async close() {
        await this.syncStreamImplementation?.dispose();
        await this.syncDisposer?.();
    }
    async connect(connector, options, serializedSchema) {
        // Keep track if there were pending operations before this call
        const hadPendingOptions = !!this.pendingConnectionOptions;
        // Update pending options to the latest values
        const resolvedOptions = resolveSyncOptions(options, this.options.defaultConnectionMethod);
        this.pendingConnectionOptions = {
            connector,
            options: resolvedOptions,
            schema: serializedSchema
        };
        if (connector.postCheckpointRequest && resolvedOptions.checkpointMode == 'legacy') {
            this.logger.log({
                level: LogLevels.warn,
                message: 'The backend connector implements postCheckpointRequest, but connect() was called without checkpoint requests enabled.'
            });
        }
        // Disconnecting here provides aborting in progress connection attempts.
        // The connectInternal method will clear pending options once it starts connecting (with the options).
        // We only need to trigger a disconnect here if we have already reached the point of connecting.
        // If we do already have pending options, a disconnect has already been performed.
        // The connectInternal method also does a sanity disconnect to prevent straggler connections.
        // We should also disconnect if we have already completed a connection attempt.
        if (!hadPendingOptions || this.syncStreamImplementation) {
            await this.disconnectInternal();
        }
        // Triggers a connect which checks if pending options are available after the connect completes.
        // The completion can be for a successful, unsuccessful or aborted connection attempt.
        // If pending options are available another connection will be triggered.
        const checkConnection = async () => {
            if (this.pendingConnectionOptions) {
                // Pending options have been placed while connecting.
                // Need to reconnect.
                this.connectingPromise = this.connectInternal()
                    .catch(() => { })
                    .finally(checkConnection);
                return this.connectingPromise;
            }
            else {
                // Clear the connecting promise, done.
                this.connectingPromise = null;
                return;
            }
        };
        this.connectingPromise ??= this.connectInternal()
            .catch(() => { })
            .finally(checkConnection);
        return this.connectingPromise;
    }
    async connectInternal() {
        let appliedOptions = null;
        // This method ensures a disconnect before any connection attempt
        await this.disconnectInternal();
        /**
         * This portion creates a sync implementation which can be racy when disconnecting or
         * if multiple tabs on web are in use.
         * This is protected in an exclusive lock.
         * The promise tracks the creation which is used to synchronize disconnect attempts.
         */
        this.syncStreamInitPromise = new Promise(async (resolve, reject) => {
            try {
                if (!this.pendingConnectionOptions) {
                    this.logger.log({
                        level: LogLevels.debug,
                        message: 'No pending connection options found, not creating sync stream implementation'
                    });
                    // A disconnect could have cleared this.
                    resolve();
                    return;
                }
                if (this.disconnectingPromise) {
                    resolve();
                    return;
                }
                const { connector, options, schema } = this.pendingConnectionOptions;
                appliedOptions = options;
                this.currentOptions = options;
                this.currentConnector = connector;
                this.pendingConnectionOptions = null;
                const subscriptionsAtStart = this.subscriptionIdentity;
                const { sync, onDispose } = await this.options.createSyncImplementation(connector, {
                    subscriptions: this.activeStreams,
                    serializedSchema: schema
                });
                this.iterateListeners((l) => l.syncStreamCreated?.(sync));
                this.syncStreamImplementation = sync;
                this.syncDisposer = onDispose;
                await this.syncStreamImplementation.waitForReady();
                // Subscriptions changed while creating the sync stream implementation, update it now.
                if (this.subscriptionIdentity !== subscriptionsAtStart) {
                    this.syncStreamImplementation.updateSubscriptions(this.activeStreams);
                }
                resolve();
            }
            catch (error) {
                reject(error);
            }
        });
        await this.syncStreamInitPromise;
        this.syncStreamInitPromise = null;
        if (!appliedOptions) {
            // A disconnect could have cleared the options which did not create a syncStreamImplementation
            return;
        }
        // It might be possible that a disconnect triggered between the last check
        // and this point. Awaiting here allows the sync stream to be cleared if disconnected.
        await this.disconnectingPromise;
        this.logger.log({ level: LogLevels.debug, message: 'Attempting to connect to PowerSync instance' });
        await this.syncStreamImplementation?.connect(appliedOptions);
    }
    /**
     * Close the sync connection.
     *
     * Use {@link ConnectionManager.connect} to connect again.
     */
    async disconnect() {
        // This will help abort pending connects
        this.pendingConnectionOptions = null;
        this.currentOptions = null;
        this.currentConnector = null;
        await this.disconnectInternal();
    }
    async disconnectInternal() {
        if (this.disconnectingPromise) {
            // A disconnect is already in progress
            return this.disconnectingPromise;
        }
        this.disconnectingPromise = this.performDisconnect();
        await this.disconnectingPromise;
        this.disconnectingPromise = null;
    }
    async performDisconnect() {
        // Wait if a sync stream implementation is being created before closing it
        // (syncStreamImplementation must be assigned before we can properly dispose it)
        await this.syncStreamInitPromise;
        // Keep reference to the sync stream implementation and disposer
        // The class members will be cleared before we trigger the disconnect
        // to prevent any further calls to the sync stream implementation.
        const sync = this.syncStreamImplementation;
        this.syncStreamImplementation = null;
        const disposer = this.syncDisposer;
        this.syncDisposer = null;
        await sync?.disconnect();
        await sync?.dispose();
        await disposer?.();
    }
    stream(adapter, name, parameters) {
        const desc = { name, parameters };
        const waitForFirstSync = (abort) => {
            return adapter.firstStatusMatching((s) => s.forStream(desc)?.subscription.hasSynced, abort);
        };
        return {
            ...desc,
            subscribe: async (options) => {
                // NOTE: We also run this command if a subscription already exists, because this increases the expiry date
                // (relevant if the app is closed before connecting again, where the last subscribe call determines the ttl).
                await adapter.rustSubscriptionsCommand({
                    subscribe: {
                        stream: {
                            name,
                            params: parameters
                        },
                        ttl: options?.ttl,
                        priority: options?.priority
                    }
                });
                if (!this.syncStreamImplementation) {
                    // We're not connected. So, update the offline sync status to reflect the new subscription.
                    // (With an active iteration, the sync client would include it in its state).
                    await adapter.resolveOfflineSyncStatus();
                }
                const key = `${name}|${JSON.stringify(parameters)}`;
                let subscription = this.locallyActiveSubscriptions.get(key);
                if (subscription == null) {
                    const clearSubscription = () => {
                        this.locallyActiveSubscriptions.delete(key);
                        this.subscriptionsMayHaveChanged();
                    };
                    subscription = new ActiveSubscription(name, parameters, this.logger, waitForFirstSync, clearSubscription);
                    this.locallyActiveSubscriptions.set(key, subscription);
                    this.subscriptionsMayHaveChanged();
                }
                return new SyncStreamSubscriptionHandle(subscription);
            },
            unsubscribeAll: async () => {
                await adapter.rustSubscriptionsCommand({ unsubscribe: { name, params: parameters } });
                this.subscriptionsMayHaveChanged();
            }
        };
    }
    /**
     * @internal exposed for testing
     */
    get activeStreams() {
        return [...this.locallyActiveSubscriptions.values()].map((a) => ({ name: a.name, params: a.parameters }));
    }
    /**
     * Identifies the current set of subscriptions, for detecting a change across an await.
     */
    get subscriptionIdentity() {
        return [...this.locallyActiveSubscriptions.keys()].join('\n');
    }
    subscriptionsMayHaveChanged() {
        this.syncStreamImplementation?.updateSubscriptions(this.activeStreams);
    }
}
class ActiveSubscription {
    name;
    parameters;
    logger;
    waitForFirstSync;
    clearSubscription;
    refcount = 0;
    constructor(name, parameters, logger, waitForFirstSync, clearSubscription) {
        this.name = name;
        this.parameters = parameters;
        this.logger = logger;
        this.waitForFirstSync = waitForFirstSync;
        this.clearSubscription = clearSubscription;
    }
    decrementRefCount() {
        this.refcount--;
        if (this.refcount == 0) {
            this.clearSubscription();
        }
    }
}
class SyncStreamSubscriptionHandle {
    subscription;
    active = true;
    constructor(subscription) {
        this.subscription = subscription;
        subscription.refcount++;
        _finalizer?.register(this, subscription, this);
    }
    get name() {
        return this.subscription.name;
    }
    get parameters() {
        return this.subscription.parameters;
    }
    waitForFirstSync(abort) {
        return this.subscription.waitForFirstSync(abort);
    }
    unsubscribe() {
        if (this.active) {
            this.active = false;
            _finalizer?.unregister(this);
            this.subscription.decrementRefCount();
        }
    }
}
const _finalizer = 'FinalizationRegistry' in globalThis
    ? new FinalizationRegistry((sub) => {
        sub.logger.log({
            level: LogLevels.warn,
            message: `A subscription to ${sub.name} with params ${JSON.stringify(sub.parameters)} leaked! Please ensure calling unsubscribe() when you don't need a subscription anymore. For global subscriptions, consider storing them in global fields to avoid this warning.`
        });
    })
    : null;
//# sourceMappingURL=ConnectionManager.js.map